import {
  IntlProvider,
  load,
  loadMessages,
  LocalizationProvider,
} from "@progress/kendo-react-intl";
import { createContext, useEffect, useState } from "react";
import useSessionStorage from "../hooks/useSessionStorage";
import { Dictionary } from "../types/Dictionary";
import { LocalizationCode } from "../types/locale/LocalizationCode";
import likelySubtags from "cldr-core/supplemental/likelySubtags.json";
import currencyData from "cldr-core/supplemental/currencyData.json";
import weekData from "cldr-core/supplemental/weekData.json";

import frNumbers from "cldr-numbers-full/main/fr/numbers.json";
import frCurrencies from "cldr-numbers-full/main/fr/currencies.json";
import frCaGregorian from "cldr-dates-full/main/fr/ca-gregorian.json";
import frDateFields from "cldr-dates-full/main/fr/dateFields.json";

import nlNumbers from "cldr-numbers-full/main/nl/numbers.json";
import nlCurrencies from "cldr-numbers-full/main/nl/currencies.json";
import nlCaGregorian from "cldr-dates-full/main/nl/ca-gregorian.json";
import nlDateFields from "cldr-dates-full/main/nl/dateFields.json";
import useBranding from "../hooks/useBranding";
import localeService from "../services/locale.service";

interface LocaleTranslation {
  locale: LocalizationCode;
  componentTranslations: Dictionary<Dictionary<string>>;
  setDateTime: Date;
}

export interface AppLocale {
  previous: LocaleTranslation;
  current: LocaleTranslation;
}

type LocaleContextType = {
  supportedLocales: LocalizationCode[];
  selectedLocale?: AppLocale;
  localeSwitchFailed: boolean;
  setUserLocale: (locale: LocalizationCode) => void;
  setComponentTranslations: (
    groupKey: string
  ) => Promise<Dictionary<string> | undefined>;
  setPreviousAppLocale: (keyGroup: string) => void;
};

export const defaultLocaleCode = {
  id: 1,
  code: "en-UK",
  displayName: "English UK",
  Country: { id: 1, name: "UK" },
};

const defaultLocale: LocaleTranslation = {
  locale: {
    id: 1,
    code: "en-UK",
    displayName: "English UK",
    Country: { id: 1, name: "UK" },
  },
  componentTranslations: {},
  setDateTime: new Date(),
};

const LocaleContext = createContext<LocaleContextType | null>(null);

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const brandingCtx = useBranding();
  const [supportedLocales, setSupportedLocales] = useState<LocalizationCode[]>(
    []
  );
  const [selectedLocale, setSelectedLocale] = useSessionStorage<
    AppLocale | undefined
  >("AppLocale", undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [kendoLocale, setKendoLocale] = useState<string>("en-US");
  const [localeSwitchFailed, setLocaleSwitchFailed] = useState<boolean>(false);

  useEffect(() => {
    const sendRequest = async () => {
      try {
        setLoading(true);
        const resp = await localeService.fetchSupportedLocales();
        setSupportedLocales(resp);
        let appLocale: AppLocale = {
          previous: defaultLocale,
          current: defaultLocale,
        };

        if (selectedLocale) {
          appLocale = selectedLocale;
        } else if (brandingCtx?.branding?.defaultLocale) {
          appLocale = {
            ...appLocale,
            current: {
              locale: brandingCtx?.branding?.defaultLocale,
              componentTranslations: {},
              setDateTime: new Date(),
            },
          };
        }
        if (brandingCtx?.branding) {
          setSelectedLocale(appLocale);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    sendRequest();
  }, [brandingCtx?.branding]);

  const setUserLocale = (locale: LocalizationCode) => {
    setLocaleSwitchFailed(false);
    const currentDate = new Date();
    if (
      locale.code !== selectedLocale?.current.locale.code ||
      new Date(selectedLocale.current.setDateTime).getDate() <
        currentDate.getDate()
    ) {
      setSelectedLocale({
        previous: selectedLocale?.current ?? defaultLocale,
        current: {
          locale,
          componentTranslations: {},
          setDateTime: currentDate,
        },
      });
      localeService
        .fetchKendoTranslations(locale.code)
        .then((res) => {
          switch (locale.code) {
            case "fr-FR": {
              load(
                likelySubtags,
                currencyData,
                weekData,
                frNumbers,
                frCurrencies,
                frCaGregorian,
                frDateFields
              );
              loadMessages(res, locale.code);
              setKendoLocale(locale.code);
              break;
            }
            case "nl-BE": {
              load(
                likelySubtags,
                currencyData,
                weekData,
                nlNumbers,
                nlCurrencies,
                nlCaGregorian,
                nlDateFields
              );
              loadMessages(res, "nl");
              setKendoLocale("nl");
              break;
            }
            default: {
              setKendoLocale("en-US");
            }
          }
        })
        .catch((err) => {
          setKendoLocale("en-US");
        });
    }
  };

  const setComponentTranslations = async (
    groupKey: string
  ): Promise<Dictionary<string> | undefined> => {
    setLocaleSwitchFailed(false);
    try {
      if (selectedLocale) {
        const resp = await localeService.fetchTranslations(
          selectedLocale.current.locale.code,
          groupKey
        );
        selectedLocale.current.componentTranslations[groupKey] = resp;
        setSelectedLocale(selectedLocale);
        return resp;
      }
    } catch (err) {
      if (selectedLocale?.current.locale.id !== 1) {
        setLocaleSwitchFailed(true);
      }
      throw err;
    }
  };

  const setPreviousAppLocale = (keyGroup: string) => {
    if (
      selectedLocale &&
      selectedLocale.previous.componentTranslations[keyGroup]
    ) {
      setSelectedLocale({
        previous: defaultLocale,
        current: selectedLocale.previous,
      });
    }
  };

  return (
    <LocaleContext.Provider
      value={{
        supportedLocales,
        selectedLocale,
        setUserLocale,
        setComponentTranslations,
        setPreviousAppLocale,
        localeSwitchFailed,
      }}
    >
      <LocalizationProvider language={kendoLocale}>
        <IntlProvider locale={kendoLocale.split("-")[0]}>
          {children}
        </IntlProvider>
      </LocalizationProvider>
    </LocaleContext.Provider>
  );
};

export default LocaleContext;
